import { MOBILE_NAV_TAB_STORAGE_KEY } from "@/components/layout/mobileNavTab.ts";
import { CALENDAR_VIEW_STORAGE_KEY } from "@/components/canvas/useSharedCalendarState.ts";
import {
  CANVAS_COLUMN_SPLIT_STORAGE_KEY,
  THREAD_LIST_WIDTH_STORAGE_KEY,
} from "@/components/layout/SplitPanel/CanvasSplitPanelLayout.tsx";
import { setRefusedNewChatDraft } from "@/components/messages/Omnibox/composerDraft.ts";
import { PREFERENCES_STORAGE_KEY } from "@/components/preferences/usePreferences.tsx";
import {
  ROUTINES_AREA_ROLE_STORAGE_KEY,
  SKILLS_AREA_ROLE_STORAGE_KEY,
} from "@/components/routines/areaRoleFilter.tsx";
import { SCHEDULING_RECENT_CONTACTS_KEY } from "@/components/typeahead/recent-contacts.ts";

/**
 * Remove the account-scoped keys before a hard reload into a *linked* account —
 * the account switcher and the add-account flow — so preferences, typeahead
 * history and layout choices from the previous account don't leak into the next.
 *
 * This is the targeted reset for staying signed in as the same user. Leaving
 * the session (logout, rejection, deleting the active account) is a different
 * boundary and takes the full wipe in `endSession.ts`, which keeps nothing but
 * the device allowlist.
 *
 * window.location.reload() clears React/in-memory state transitively, but it
 * does NOT clear localStorage. Without this, the switcher briefly shows the
 * old account's preferences on reload (stale-while-revalidate), the typeahead
 * pre-populates with the old account's recent contacts, and the canvas divider
 * stays where the previous user dragged it.
 */
export function clearClientState(): void {
  localStorage.removeItem(PREFERENCES_STORAGE_KEY);
  localStorage.removeItem(SCHEDULING_RECENT_CONTACTS_KEY);
  localStorage.removeItem(THREAD_LIST_WIDTH_STORAGE_KEY);
  localStorage.removeItem(CANVAS_COLUMN_SPLIT_STORAGE_KEY);
  // The name the thread list's width used when the number under it meant the
  // space beside the list instead. Nothing reads it any more; this is what stops
  // it sitting in storage forever.
  localStorage.removeItem("canvasSplitWidth");
  localStorage.removeItem(MOBILE_NAV_TAB_STORAGE_KEY);
  localStorage.removeItem(CALENDAR_VIEW_STORAGE_KEY);
  localStorage.removeItem(ROUTINES_AREA_ROLE_STORAGE_KEY);
  localStorage.removeItem(SKILLS_AREA_ROLE_STORAGE_KEY);
  // Session storage outlives the same-tab reload too, and this text was
  // refused for the previous account. The setter swallows blocked storage, so
  // it cannot stop the reload this runs ahead of.
  setRefusedNewChatDraft(undefined);
}
